import React from 'react';
import { Link } from 'react-router-dom';
import {
  Link as LinkIcon,
  Database,
  Package,
  Users,
  Lock,
  Zap,
  ArrowRight,
  Globe,
  Shield,
  Settings,
  CheckCircle
} from 'lucide-react';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';

const IntegrationLanding = () => {
  const integrationFeatures = [
    {
      id: 'erp',
      title: 'ERP Integration',
      description: 'Seamlessly connect with SAP, Oracle, NetSuite & other ERP systems',
      icon: <Database size={24} />,
      path: '/integration/erp',
      color: '#3b82f6',
      gradient: 'linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%)',
      features: ['Real-time sync', 'Data mapping', 'Error handling']
    },
    {
      id: 'procurement',
      title: 'Procurement Integration',
      description: 'Link purchase contracts with procurement systems',
      icon: <Package size={24} />,
      path: '/integration/procurement',
      color: '#10b981',
      gradient: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)',
      features: ['Purchase orders', 'Vendor sync', 'Cost tracking']
    },
    {
      id: 'crm',
      title: 'CRM Integration',
      description: 'Connect with Salesforce, HubSpot & other CRM platforms',
      icon: <Users size={24} />,
      path: '/integration/crm',
      color: '#8b5cf6',
      gradient: 'linear-gradient(135deg, #8b5cf6 0%, #a78bfa 100%)',
      features: ['Customer data', 'Sales pipeline', 'Contact sync']
    },
    {
      id: 'sso',
      title: 'SSO & Access Control',
      description: 'Enterprise authentication with LDAP, AD & SSO providers',
      icon: <Lock size={24} />,
      path: '/integration/sso',
      color: '#f59e0b',
      gradient: 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)',
      features: ['Single sign-on', 'LDAP/AD sync', 'Role mapping']
    },
    {
      id: 'api',
      title: 'API Management',
      description: 'REST APIs & webhooks for custom integrations',
      icon: <Zap size={24} />,
      path: '/integration/api',
      color: '#ef4444',
      gradient: 'linear-gradient(135deg, #ef4444 0%, #f87171 100%)',
      features: ['REST APIs', 'Webhooks', 'Custom endpoints']
    }
  ];

  const stats = [
    { label: 'Connected Systems', value: '24', icon: <LinkIcon size={20} /> },
    { label: 'Data Syncs', value: '1.2M', icon: <Database size={20} /> },
    { label: 'API Calls', value: '89K', icon: <Zap size={20} /> },
    { label: 'Uptime', value: '99.9%', icon: <CheckCircle size={20} /> }
  ];

  const supportedSystems = [
    { name: 'SAP', logo: 'SAP', category: 'ERP' },
    { name: 'Oracle', logo: 'Oracle', category: 'ERP' },
    { name: 'NetSuite', logo: 'NetSuite', category: 'ERP' },
    { name: 'Salesforce', logo: 'Salesforce', category: 'CRM' },
    { name: 'HubSpot', logo: 'HubSpot', category: 'CRM' },
    { name: 'Microsoft', logo: 'Microsoft', category: 'SSO' },
    { name: 'Google', logo: 'Google', category: 'SSO' },
    { name: 'Okta', logo: 'Okta', category: 'SSO' }
  ];

  return (
    <>
      {/* Breadcrumb - Outside page-container to align with sidebar */}
      <PageBreadcrumb />
      {/* Page Header - Outside page-container to align with sidebar */}
      <PageHeader
        title="Integration Hub"
        description="Connect CAM360 with your existing business systems. Seamless integrations with ERP, CRM, SSO, and custom applications for unified data management."
      />
      <div className="page-container">
        {/* KPI Cards */}
        <div className="page-stats-grid" style={{ marginBottom: '32px' }}>
          <div className="page-stat-card">
            <div className="card h-100 page-kpi-card">
              <div className="card-body page-kpi-body">
                <div className="page-kpi-content">
                  <div className="page-kpi-text">
                    <h3 className="page-kpi-value color-1">{stats[0].value}</h3>
                    <p className="page-kpi-label">{stats[0].label}</p>
                  </div>
                  <div className="page-kpi-icon-container color-1">
                    <LinkIcon size={24} className="page-kpi-icon color-1" />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="page-stat-card">
            <div className="card h-100 page-kpi-card">
              <div className="card-body page-kpi-body">
                <div className="page-kpi-content">
                  <div className="page-kpi-text">
                    <h3 className="page-kpi-value color-2">{stats[1].value}</h3>
                    <p className="page-kpi-label">{stats[1].label}</p>
                  </div>
                  <div className="page-kpi-icon-container color-2">
                    <Database size={24} className="page-kpi-icon color-2" />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="page-stat-card">
            <div className="card h-100 page-kpi-card">
              <div className="card-body page-kpi-body">
                <div className="page-kpi-content">
                  <div className="page-kpi-text">
                    <h3 className="page-kpi-value color-3">{stats[2].value}</h3>
                    <p className="page-kpi-label">{stats[2].label}</p>
                  </div>
                  <div className="page-kpi-icon-container color-3">
                    <Zap size={24} className="page-kpi-icon color-3" />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="page-stat-card">
            <div className="card h-100 page-kpi-card">
              <div className="card-body page-kpi-body">
                <div className="page-kpi-content">
                  <div className="page-kpi-text">
                    <h3 className="page-kpi-value color-4">{stats[3].value}</h3>
                    <p className="page-kpi-label">{stats[3].label}</p>
                  </div>
                  <div className="page-kpi-icon-container color-4">
                    <CheckCircle size={24} className="page-kpi-icon color-4" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Integration Management Cards */}
        <div
          className="grid-modern-3"
          style={{
            maxWidth: '1200px',
            margin: '0 auto',
            width: '100%'
          }}
        >
          {integrationFeatures.map((card) => (
            <div
              key={card.id}
              className="asset-landing-card"
              onClick={() => window.location.href = card.path}
            >
              {/* Top Accent Line */}
              <div
                style={{
                  height: '4px',
                  background: `var(--gold-primary)`,
                  width: '100%'
                }}
              />

              {/* Card Content */}
              <div style={{ padding: '24px' }}>
                {/* Icon and Title */}
                <div className="d-flex align-items-center mb-3">
                  <div
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '10px',
                      background: '#FFF9E6',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginRight: '16px'
                    }}
                  >
                    <div style={{ color: 'var(--gold-primary)' }} className="asset-card-icon">
                      {card.icon}
                    </div>
                  </div>
                  <div className="flex-grow-1">
                    <h4 style={{
                      fontSize: '18px',
                      fontWeight: '600',
                      color: 'var(--text-primary)',
                      margin: '0',
                      fontFamily: 'var(--font-family)'
                    }}>
                      {card.title}
                    </h4>
                  </div>
                  <ArrowRight
                    size={20}
                    className="asset-card-arrow"
                  />
                </div>

                {/* Description */}
                <p style={{
                  fontSize: '13px',
                  color: 'var(--text-secondary)',
                  margin: '0 0 12px 0',
                  lineHeight: '1.4',
                  fontFamily: 'var(--font-family)'
                }}>
                  {card.description}
                </p>

                {/* Features */}
                <div>
                  <div style={{
                    fontSize: '11px',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '6px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>
                    Key Features
                  </div>
                  <div className="d-flex flex-wrap gap-1">
                    {card.features.map((feature, index) => (
                      <span
                        key={index}
                        style={{
                          fontSize: '12px',
                          padding: '4px 8px',
                          background: '#f3f4f6',
                          color: '#374151',
                          borderRadius: '6px',
                          fontWeight: '500',
                        }}
                      >
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default IntegrationLanding;
